import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  Form,
  Space,
  Typography,
  message,
  Tabs,
  Divider,
} from "antd";
import { ArrowLeftOutlined, ExclamationCircleOutlined, SaveOutlined, FileTextOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import api from "../services/api";
import { useAppsList, useTemplatesAllVersions, useLandingTemplates, useApp, useStaticSites, useTemplateDynamicFields, fetchTemplateDynamicFields, getTemplateDynamicFieldsQueryKey, useDeleteAppMutation, useDeployAppMutation, useDeployBulkMutation, useDeleteBulkMutation, useCreateAppMutation, useUpdateAppMutation, useSyncAppEndpointsMutation, useRefreshAppsMutation, useAppConfigTemplates } from "../hooks/useAppsQueries";
import {
  App,
  MappingLike,
  RouteLike,
  DeployBulkResult,
} from "../types/api";
import { TemplateDynamicFields, EndpointMapping, DynamicField, TemplateEndpointInfo } from "../types";
import { AppsTable } from "../components/AppsTable";
import { WorkerCodeModal } from "../components/WorkerCodeModal";
import { SaveAsTemplateModal } from "../components/SaveAsTemplateModal";
import { LoadTemplateModal } from "../components/LoadTemplateModal";
import { BasicInfoTab, ConfigTab, RoutingTab, type RoutingTabProps, LandingCreationContentTab, DomainFilesTab } from "../components/AppsForm";
import { inflateFromApi, validateEndpointMappingsForJson, clearOfferFields, getApiErrorForModal, extractApiError, parseJsonArray } from "@/helpers/helpers.app";
import { ResponseType, StaticSiteStatus } from "../types/enums";
import { parseDynamicFieldValue } from "@/utils/dynamicFields";
import { findApiVsStaticConflicts } from "@/utils/appUtils";
const { Title, Text } = Typography;

/**
 * Конвертує об'єкт dynamicFields з форми в масив для EndpointMappingsComponent
 */
function convertDynamicFieldsToArray(fields: Record<string, any>): Array<{ name: string, value: string }> {
  return Object.entries(fields).map(([name, value]) => ({
    name,
    value: String(value || "")
  }));
}

/**
 * Нормалізує один endpoint mapping з UI-форми у формат бекенда (EndpointMappingDto).
 * Викидає legacy-поля (responseType/responseCode/errorType/errorCode).
 * Використовується і при сабміті апки, і при збереженні пресету роутингу.
 */
function mapEndpointMappingToBackend(m: any): any {
  const {
    handler,
    path,
    method,
    enabled,
    subdomain,

    isOffer,
    offerResponseType,
    offerResponseCode,
    offerUrl,

    offerErrorType,
    offerErrorCode,

    errorStaticSiteId,
    errorLandingIndexPath,

    encryptionAlgorithm,
  } = m;

  const out: any = {
    handler,
    path,
    method,
  };

  out.enabled = enabled !== undefined ? Boolean(enabled) : true;

  if (subdomain !== undefined) {
    out.subdomain = String(subdomain);
  }

  const finalIsOffer = isOffer !== undefined ? Boolean(isOffer) : false;
  out.isOffer = finalIsOffer;

  // Offer-поля тільки для оферних функцій
  if (finalIsOffer) {
    if (offerResponseType !== undefined) {
      out.offerResponseType = String(offerResponseType);
    }

    if (offerUrl !== undefined) {
      out.offerUrl = String(offerUrl);
    }

    if (offerResponseCode !== undefined && offerResponseCode !== null) {
      out.offerResponseCode = Number(offerResponseCode);
    }

    if (offerErrorType !== undefined) {
      const type = String(offerErrorType);
      out.offerErrorType = type === "landing" ? "render" : type;

      if (
        out.offerErrorType === "redirect" ||
        out.offerErrorType === "json"
      ) {
        if (offerErrorCode !== undefined && offerErrorCode !== null) {
          out.offerErrorCode = Number(offerErrorCode);
        }

        if (m.offerError !== undefined && m.offerError !== null) {
          out.offerError = String(m.offerError);
        }
      } else if (out.offerErrorType === "encrypted") {
        // Encrypted тип: копіюємо offerError як JSON template
        if (offerErrorCode !== undefined && offerErrorCode !== null) {
          out.offerErrorCode = Number(offerErrorCode);
        }

        if (m.offerError !== undefined && m.offerError !== null) {
          out.offerError = String(m.offerError);
        }
      }

      if (out.offerErrorType === "render") {
        out.offerError = JSON.stringify({
          staticSiteId: String(errorStaticSiteId || ""),
          indexPath: String(errorLandingIndexPath || "index.html"),
        });

        out.errorStaticSiteId = String(errorStaticSiteId || "");
        out.errorLandingIndexPath = String(errorLandingIndexPath || "index.html");

        if (offerErrorCode !== undefined && offerErrorCode !== null) {
          out.offerErrorCode = Number(offerErrorCode);
        }
      }
    }
  }

  if (
    encryptionAlgorithm !== undefined &&
    encryptionAlgorithm !== null &&
    (out.offerResponseType === "encrypted" || out.offerErrorType === "encrypted")
  ) {
    out.encryptionAlgorithm = String(encryptionAlgorithm);
  }

  return out;
}
export const Apps: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Визначаємо режим на основі URL
  const isCreateMode = location.pathname.includes("/create");
  const isEditMode = location.pathname.includes("/edit") && !!id;
  const isFormMode = isCreateMode || isEditMode;

  const queryClient = useQueryClient();
  const appsQuery = useAppsList();
  const templatesQuery = useTemplatesAllVersions();
  const landingTemplatesQuery = useLandingTemplates();
  const editAppId = isEditMode && id ? id : null;
  const appQuery = useApp(editAppId);
  const refreshAppsMutation = useRefreshAppsMutation();
  const staticSitesQuery = useStaticSites(editAppId);
  const deleteAppMutation = useDeleteAppMutation();
  const deployAppMutation = useDeployAppMutation();
  const deployBulkMutation = useDeployBulkMutation();
  const deleteBulkMutation = useDeleteBulkMutation();
  const createAppMutation = useCreateAppMutation();
  const updateAppMutation = useUpdateAppMutation();
  const syncEndpointsMutation = useSyncAppEndpointsMutation();
  const appConfigTemplatesQuery = useAppConfigTemplates();
  const purgeLfCacheMutation = useMutation({
    mutationFn: (appId: string) => api.purgeLfCache(appId),
    onSuccess: (data) => {
      message.success(
        `Кеш LF скинуто: видалено ${data.deleted} з ${data.matched} ключів (${data.pattern})`,
      );
    },
    onError: (err: any) => {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Помилка скидання кешу LF",
      );
    },
  });
  const apps = appsQuery.data?.apps ?? [];
  const allowedAppIds = appsQuery.data?.allowedAppIds ?? [];
  const templates = templatesQuery.data ?? [];
  /** customId апок, які вже є в Gray Manager (для позначки в селекті CRM ID). */
  const existingCustomIds = useMemo(
    () => new Set(apps.map((a) => String(a.customId ?? "").trim().toLowerCase())),
    [apps],
  );

  const [loadingAction, setLoadingAction] = useState(false);
  const loading =
    loadingAction ||
    appsQuery.isLoading ||
    templatesQuery.isLoading ||
    landingTemplatesQuery.isLoading ||
    (!!editAppId && (appQuery.isLoading || staticSitesQuery.isLoading));
  const [isSubmitting, setIsSubmitting] = useState(false); // Захист від подвійного виклику handleSubmit
  /** У режимі редагування — дані з React Query; у create — null. Джерело правди: appQuery. */
  const editingApp = isEditMode && editAppId ? (appQuery.data ?? null) : null;
  /** appId апки, для якої відкрита модалка з кодом воркера; null = модалка закрита. */
  const [workerCodeAppId, setWorkerCodeAppId] = useState<string | null>(null);
  /** Стан завантаження коду воркера (для кнопки в таблиці); оновлюється WorkerCodeModal через onLoadingChange. */
  const [workerCodeLoading, setWorkerCodeLoading] = useState(false);
  const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
  const [loadTemplateModalOpen, setLoadTemplateModalOpen] = useState(false);
  const [form] = Form.useForm();

  const selectedTemplateId = Form.useWatch("templateId", form);
  const templateDynamicFieldsQuery = useTemplateDynamicFields(selectedTemplateId);
  /** Джерело правди для UI: тільки React Query (templateDynamicFieldsQuery.data). */
  const effectiveTemplateDynamicFields = templateDynamicFieldsQuery.data ?? null;
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [dynamicFieldsValues, setDynamicFieldsValues] = useState<
    Record<string, string>
  >({});
  const [endpointMappingsConfig, setEndpointMappingsConfig] = useState<
    EndpointMapping[]
  >([]);
  const [lastLoadedTemplateVersion, setLastLoadedTemplateVersion] = useState<
    string | null
  >(null);
  const [endpointMappingsKey, setEndpointMappingsKey] = useState<number>(0);
  /** Останні ендпоінти після синхронізації — щоб value оновився в тому ж тіку (React батчить setState). */
  const lastSyncedEndpointsRef = useRef<EndpointMapping[] | null>(null);
  const editEndpointMappingsFromDbRef = useRef<EndpointMapping[] | null>(null);
  const isPresetLoadingRef = useRef(false);
  /** Шаблон щойно застосованого пресету. Прапорець isPresetLoadingRef знімається
   *  тільки коли користувач ОБРАВ ІНШИЙ шаблон — тому ефекти зміни шаблону
   *  не можуть затерти роутинг пресету жодною пізньою появою (rerender/refetch). */
  const presetTemplateIdRef = useRef<string | null>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  /** Результат масової операції (деплой або видалення) — одна модалка для обох */
  const [bulkActionResult, setBulkActionResult] = useState<{
    kind: "deploy" | "delete";
    result: DeployBulkResult;
  } | null>(null);
  const [singleDeployError, setSingleDeployError] = useState<{
    appId: string;
    error: string;
    statusCode?: number;
  } | null>(null);

  const dynamicFieldsWatch = Form.useWatch("dynamicFields", form) || {};
  /** У режимі редагування — staticSites з React Query; у create — []. Джерело правди: staticSitesQuery. */
  const staticSites = isEditMode && editAppId ? (staticSitesQuery.data ?? []) : [];

  useLayoutEffect(() => {
    const freshApp = appQuery.data;
    if (!isEditMode || !id) return;
    if (!freshApp || freshApp.id !== id) {
      editEndpointMappingsFromDbRef.current = null;
      setEndpointMappingsConfig([]);
      form.setFieldsValue({ endpointMappings: [] });
      setEndpointMappingsKey((k) => k + 1);
      return;
    }
    // Якщо щойно застосовано пресет роутингу — НЕ затираємо форму даними з БД.
    // Цей ефект спрацьовує на КОЖЕН рефетч appQuery (фокус вікна, staleTime, інвалідації),
    // тому без захисту кастомні шляхи пресету (напр. /api/nitro-lesson) поверталися
    // до збережених у БД дефолтів шаблону (/handle-get тощо).
    if (isPresetLoadingRef.current) return;
    form.setFieldsValue({ dynamicFields: {} });
    setDynamicFieldsValues({});
    let parsedEndpointMappings: EndpointMapping[] = [];
    if (freshApp?.endpointMappings) {
      try {
        if (typeof freshApp.endpointMappings === "string") {
          parsedEndpointMappings = JSON.parse(freshApp.endpointMappings);
        } else if (Array.isArray(freshApp.endpointMappings)) {
          parsedEndpointMappings = freshApp.endpointMappings;
        }
      } catch (error) {
        console.warn("Failed to parse endpointMappings from app:", error);
      }
    }
    const normalizedEndpointMappings = parsedEndpointMappings.map((m: any) => inflateFromApi(m));
    editEndpointMappingsFromDbRef.current = normalizedEndpointMappings;
    form.setFieldsValue({
      endpointMappings: normalizedEndpointMappings,
      templateId: freshApp.templateId ?? "",
      greyType: freshApp.greyType ?? "api",
      domain: freshApp.domain ?? "",
      name: freshApp.name ?? "",
      customId: freshApp.customId ?? "",
    });
    setEndpointMappingsConfig(normalizedEndpointMappings);
  }, [id, isEditMode, appQuery.data]);

  useEffect(() => {
    const freshApp = appQuery.data;
    if (!isEditMode || !id) return;
    if (freshApp == null || freshApp.id !== id) return;
    // Поки активний застосований пресет — не синкаємо форму з БД:
    // інакше цей ефект перезапише динамічні поля значеннями з бази,
    // а пресет має чіпати ТІЛЬКИ роутинг, не торкаючись конфігурації.
    if (isPresetLoadingRef.current) return;
    if (freshApp.templateId) {
      loadTemplateDynamicFields(freshApp.templateId, false, false, freshApp);
    }
  }, [id, isEditMode, appQuery.data, staticSitesQuery.data]);

  // Load dynamic fields when template is selected
  // Load dynamic fields when template is selected
  useEffect(() => {
    if (!isFormMode) return;
    if (isEditMode && id && !editingApp) return;

    // Якщо завантажуємо пресет — не скидаємо форму, а відразу підтягуємо структуру шаблону.
    // forceReload = false: пресет вже містить готовий роутинг (шляхи/методи/відповіді),
    // тому endpointMappings НЕ пересобираємо з дефолтів шаблону — інакше кастомні шляхи
    // пресету (напр. /api/nitro-lesson) перезаписуються дефолтними path шаблону.
    if (isPresetLoadingRef.current) {
      if (selectedTemplateId && selectedTemplateId === presetTemplateIdRef.current) {
        // routingOnly = true: пресет застосовує ТІЛЬКИ роутинг (ендпоінти + лендинг-маршрути),
        // конфігурацію апки (динамічні поля, домен тощо) не перезаписуємо.
        loadTemplateDynamicFields(selectedTemplateId, false, false, undefined, true);
        return;
      }
      // Користувач відхилився від пресетного шаблону (обрав інший або очистив) —
      // знімаємо захист і обробляємо як звичайну зміну шаблону нижче.
      isPresetLoadingRef.current = false;
      presetTemplateIdRef.current = null;
    }

    if (!selectedTemplateId || selectedTemplateId === "") {
      // У режимі редагування не скидаємо при порожньому templateId — форму заповнить sync-ефект з appQuery.data.
      // Інакше при повторному заході в апку форма спочатку порожня, цей ефект очищає endpointMappings, і вони не підтягуються.
      if (isEditMode && id) return;
      // пользователь убрал шаблон
      resetTemplateState({
        resetFormFields: true,
        resetEndpointMappingsKey: 'increment',
        resetOnlyEndpointMappings: true,
      });
      return;
    }

    // 🔥 ВИПРАВЛЕННЯ: Перевіряємо чи вже є endpointMappings в формі (з бази через loadData)
    const existingMappings = form.getFieldValue("endpointMappings") || [];
    const hasExistingMappings = Array.isArray(existingMappings) && existingMappings.length > 0;

    // мы редактируем существующее приложение
    const isInitialLoad =
      isEditMode &&
      editingApp &&
      editingApp.templateId === selectedTemplateId;

    // 🔥 ВИПРАВЛЕННЯ: Також вважаємо це ініціалізацією, якщо є налаштування в формі
    const isInitialization = isInitialLoad || (isEditMode && hasExistingMappings);

    if (isInitialization) {
      if (isEditMode && editingApp) {
        if (selectedTemplateId !== editingApp.templateId) return;
        return;
      }
      loadTemplateDynamicFields(selectedTemplateId, /* forceReload */ false);
      return;
    }

    resetTemplateState({
      resetFormFields: true,
      resetEndpointMappingsKey: 'increment',
    });

    loadTemplateDynamicFields(selectedTemplateId, true, true);
  }, [selectedTemplateId, editingApp, isEditMode, isFormMode, form]);

  // Відстежуємо зміни версії шаблону для автоматичного оновлення полів
  useEffect(() => {
    // Під час застосування пресету — не перезавантажуємо: пресет містить готовий роутинг
    if (isPresetLoadingRef.current) {
      return;
    }
    if (selectedTemplateId && templates.length > 0) {
      const selectedTemplate = templates.find(
        (t) => t.id === selectedTemplateId
      );
      const currentVersion =
        selectedTemplate?.currentVersion || selectedTemplate?.version;

      // Якщо версія шаблону змінилася, перезавантажуємо поля
      if (
        lastLoadedTemplateVersion &&
        currentVersion &&
        lastLoadedTemplateVersion !== currentVersion
      ) {
        loadTemplateDynamicFields(selectedTemplateId, true);
      }
    }
  }, [selectedTemplateId, templates, lastLoadedTemplateVersion]);

  // Додатковий useEffect для форсування оновлення при зміні шаблону
  // Тільки для випадків, коли шаблон реально змінився (не при ініціалізації)
  const [previousTemplateId, setPreviousTemplateId] = useState<string | null>(
    null
  );

  useEffect(() => {
    // Якщо завантажуємо пресет — не скидаємо форму
    if (isPresetLoadingRef.current) {
      return;
    }

    // 🔥 ВИПРАВЛЕННЯ: Не виконувати при першому рендері або якщо це ініціалізація редагування
    if (!previousTemplateId || !selectedTemplateId || selectedTemplateId === "") {
      setPreviousTemplateId(selectedTemplateId);
      return;
    }

    if (selectedTemplateId !== previousTemplateId) {
      // 🔥 ВИПРАВЛЕННЯ: Перевіряємо чи це реальна зміна користувачем, а не ініціалізація
      // Якщо це режим редагування і вже є налаштування в формі, не скидаємо їх
      const existingMappings = form.getFieldValue("endpointMappings") || [];
      const hasExistingMappings = Array.isArray(existingMappings) && existingMappings.length > 0;
      const isUserChange = !isEditMode || !hasExistingMappings;

      if (isUserChange) {
        // ВАЖЛИВО: Скидаємо динамічні поля та ендпоінти при зміні шаблону користувачем
        resetTemplateState({
          resetFormFields: true,
          resetEndpointMappingsKey: 'increment',
        });
        // Форсуємо перезавантаження шаблонів та динамічних полів
        loadTemplateDynamicFields(selectedTemplateId, true, true);
      }
    }
    setPreviousTemplateId(selectedTemplateId);
  }, [selectedTemplateId, isEditMode, form, id]); // Додаємо id в залежності для правильної роботи при зміні апки

  // Ініціалізація форми на основі режиму
  useEffect(() => {
    if (isCreateMode) {
      setDynamicFieldsValues({});
      form.resetFields();
      form.setFieldsValue({
        logging: true,
        offerUrl: "{{OFFER_URL}}",
        offerError: "{{OFFER_ERROR}}",
        responseType: ResponseType.JSON,
        errorRedirectUrl: "",
        landingRoutes: [],
        customId: ''
      });
    }
    // УВАГА: `apps` у залежностях НЕ потрібен — у тілі ефекту не використовується,
    // а його нова референція під час завантаження списку апок перезапускала ефект
    // і робила form.resetFields() — затираючи, зокрема, щойно застосований пресет.
  }, [isCreateMode, isEditMode, id, form]);

  // Очищаємо стан шаблону при переході між апками
  useEffect(() => {
    if (!id) {
      resetTemplateState({
        resetPreviousTemplateId: true,
        resetFormFields: false,
        resetEndpointMappingsKey: true,
      });
      return;
    }

    // Для режиму редагування: очищаємо тільки якщо editingApp ще не встановлений
    // або це інша апка (щоб уникнути race condition з loadData)
    if (isEditMode) {
      // Перевіряємо, чи це інша апка
      if (editingApp && editingApp.id !== id) {
        resetTemplateState({
          resetPreviousTemplateId: true,
          resetFormFields: true,
          resetEndpointMappingsKey: true,
        });
      }
    } else {
      resetTemplateState({
        resetPreviousTemplateId: true,
        resetFormFields: false,
        resetEndpointMappingsKey: true,
      });
    }
  }, [id, isEditMode, editingApp?.id, form]); // Додаємо form для доступу до form.setFieldsValue

  // 🔥 ВИПРАВЛЕННЯ: Функція для очищення стану шаблону та ендпоінтів
  const resetTemplateState = (options: {
    resetPreviousTemplateId?: boolean;
    resetFormFields?: boolean;
    resetEndpointMappingsKey?: boolean | 'increment';
    resetOnlyEndpointMappings?: boolean;
  } = {}) => {
    const {
      resetPreviousTemplateId = false,
      resetFormFields = false,
      resetEndpointMappingsKey = true,
      resetOnlyEndpointMappings = false,
    } = options;

    if (resetPreviousTemplateId) {
      setPreviousTemplateId(null);
    }

    if (!resetOnlyEndpointMappings) {
      setDynamicFieldsValues({});
    }

    // Не очищаємо endpointMappingsConfig, якщо в формі є дані та resetFormFields: false
    // Це запобігає втраті даних після синхронізації, коли форма містить актуальні дані
    const currentFormMappings = form.getFieldValue("endpointMappings") || [];
    if (resetFormFields || currentFormMappings.length === 0) {
      setEndpointMappingsConfig([]);
      lastSyncedEndpointsRef.current = null;
    }

    if (resetEndpointMappingsKey === true) {
      setEndpointMappingsKey(0);
    } else if (resetEndpointMappingsKey === 'increment') {
      setEndpointMappingsKey((prev) => prev + 1);
    }

    if (resetFormFields) {
      form.setFieldsValue({
        endpointMappings: [],
        ...(resetOnlyEndpointMappings ? {} : { dynamicFields: {} }),
      });
    }
  };

  // Load endpoints with loading state
  const loadTemplateEndpoints = async (
    templateId: string,
    showLoader = false,
    appId?: string
  ) => {
    try {
      if (showLoader) {
        setLoadingEndpoints(true);
      }

      const currentFieldValues = { ...dynamicFieldsValues };
      const currentEndpointMappings =
        form.getFieldValue("endpointMappings") || [];

      queryClient.invalidateQueries({ queryKey: ["templates", "all-versions"] });

      // ВАЖЛИВО: В режимі редагування НЕ робимо запит на /dynamic-fields
      // Використовуємо тільки endpoints з React Query кешу, які вже завантажені через loadTemplateDynamicFields
      let dynamicFields = queryClient.getQueryData<TemplateDynamicFields>(getTemplateDynamicFieldsQueryKey(templateId)) ?? null;

      // Якщо endpoints ще не завантажені, робимо запит тільки для створення (через React Query кеш)
      if (!dynamicFields?.endpoints && !appId) {
        dynamicFields = await fetchTemplateDynamicFields(queryClient, templateId);
      } else if (appId) {
        if (!dynamicFields) {
          dynamicFields = {
            template: { id: templateId, name: '', category: '' },
            endpoints: [],
            dynamicFields: [],
            exports: []
          };
        }
      }

      // Відновлюємо збережені значення полів (тільки ті, що існують в новому шаблоні)
      if (dynamicFields?.dynamicFields) {
        const validFieldNames = dynamicFields.dynamicFields.map((f: any) =>
          f.name.toLowerCase()
        );
        const preservedValues: Record<string, string> = {};

        Object.entries(currentFieldValues).forEach(([key, value]) => {
          if (validFieldNames.includes(key.toLowerCase()) && value) {
            preservedValues[key] = value;
          }
        });

        setDynamicFieldsValues(preservedValues);
      }

      // Об'єднуємо існуючі налаштування ендпоінтів з новими ендпоінтами з шаблону
      if (dynamicFields?.endpoints) {
        const templateEndpoints = dynamicFields.endpoints;
        const existingMappings = currentEndpointMappings || [];

        // Створюємо мапу існуючих налаштувань по handler
        const existingMappingsMap = new Map<string, EndpointMapping>();
        existingMappings.forEach((mapping: EndpointMapping) => {
          existingMappingsMap.set(mapping.handler, mapping);
        });

        // Створюємо об'єднаний список ендпоінтів
        const mergedMappings = templateEndpoints.map((endpoint: TemplateEndpointInfo) => {
          const existingMapping = existingMappingsMap.get(endpoint.name);
          const isOffer = endpoint.isOffer || false;

          if (existingMapping) {

            const updatedMapping: EndpointMapping = {
              ...existingMapping,
              // Оновлюємо тільки базові поля з шаблону, зберігаючи користувацькі налаштування
              method: existingMapping.method || endpoint.method,
              path: existingMapping.path || endpoint.path,
              handler: endpoint.name,
              isOffer,
            };

            // Якщо функція стала не оферною, очищаємо offer-поля
            if (!isOffer) {
              Object.assign(updatedMapping, clearOfferFields(updatedMapping));
            }

            return updatedMapping;
          } else {
            const newMapping: EndpointMapping = {
              path: endpoint.path,
              method: endpoint.method,
              enabled: endpoint.enabled,
              handler: endpoint.name,
              isOffer,
              subdomain: "",
            };

            // Offer-поля тільки для оферних функцій
            if (isOffer) {
              newMapping.offerUrl = "";
              newMapping.offerError = "";
              newMapping.offerErrorCode = 400;
              newMapping.offerErrorType = "redirect";
              newMapping.offerResponseCode = 200;
              newMapping.offerResponseType = "redirect";
              newMapping.errorLandingSource = "staticSite";
              newMapping.errorStaticSiteId = "";
              newMapping.errorLandingIndexPath = "index.html";
            } else {
              // Для не оферних функцій очищаємо offer-поля
              Object.assign(newMapping, clearOfferFields(newMapping));
            }

            return newMapping;
          }
        });

        // Оновлюємо форму з об'єднаними налаштуваннями
        form.setFieldsValue({ endpointMappings: mergedMappings });
        setEndpointMappingsConfig(mergedMappings);
      }

      // Оновлюємо ендпоінти
      setEndpointMappingsKey((prev) => prev + 1);

    } catch (error) {
      message.error("Помилка завантаження ендпоінтів шаблону");
    } finally {
      if (showLoader) {
        setLoadingEndpoints(false);
      }
    }
  };

  // Load template dynamic fields
  // appForMerge: при виклику з sync-ефекту передаємо апку, щоб не покладатися на editingApp з closure (після reload editingApp ще null)
  const loadTemplateDynamicFields = async (
    templateId: string,
    forceReload = false,
    skipAppId = false,
    appForMerge?: App | null,
    routingOnly = false
  ) => {
    try {
      // Визначаємо, чи беремо дані з кешу (без запиту) або робимо fetch
      let dynamicFields: TemplateDynamicFields;

      if (!forceReload) {
        const cached = queryClient.getQueryData<TemplateDynamicFields>(getTemplateDynamicFieldsQueryKey(templateId));
        const hasCached = Boolean(
          cached?.template?.id === templateId &&
          cached?.dynamicFields &&
          cached?.endpoints
        );

        if (hasCached && !appForMerge) {
          // Структура вже в кеші, апки для мерджу немає — нічого не робимо.
          // Але оновлюємо версію шаблону, щоб version-effect не спрацював пізніше
          // з forceReload=true і не перезаписав роутинг (напр. щойно застосований пресет).
          const cachedTemplate = templates.find((t) => t.id === templateId);
          setLastLoadedTemplateVersion(
            cachedTemplate?.currentVersion || cachedTemplate?.version || null
          );
          return;
        }
        if (hasCached && appForMerge) {
          // Після Cancel: sync-ефект передав freshApp (збережені дані з БД). Використовуємо кеш і мерджимо з appForMerge, щоб форма отримала збережені значення.
          dynamicFields = cached!;
        } else {
          if (templateId === selectedTemplateId && templateDynamicFieldsQuery.isFetching) {
            // Запит уже йде — не дублюємо; версію також фіксуємо (див. коментар вище)
            const fetchingTemplate = templates.find((t) => t.id === templateId);
            setLastLoadedTemplateVersion(
              fetchingTemplate?.currentVersion || fetchingTemplate?.version || null
            );
            return;
          }
          dynamicFields = await fetchTemplateDynamicFields(queryClient, templateId);
        }
      } else {
        queryClient.invalidateQueries({ queryKey: ["templates", "all-versions"] });
        queryClient.invalidateQueries({ queryKey: getTemplateDynamicFieldsQueryKey(templateId) });
        dynamicFields = await fetchTemplateDynamicFields(queryClient, templateId);
      }

      // Пресет роутингу: підтягуємо ТІЛЬКИ структуру шаблону (кеш + версія для
      // version-effect). Конфігурацію (динамічні поля) та роутинг форми не чіпаємо —
      // пресет застосовує виключно роутинг.
      if (routingOnly) {
        const presetTemplate = templates.find((t) => t.id === templateId);
        setLastLoadedTemplateVersion(
          presetTemplate?.currentVersion || presetTemplate?.version || null
        );
        return;
      }

      // В режимі редагування: підставляємо значення з апки (appForMerge при виклику з sync, інакше editingApp).
      // Працюємо з копією, щоб не мутувати об'єкт у React Query кеші.
      const appToMerge = appForMerge ?? editingApp;
      let dynamicFieldsForForm = dynamicFields;
      if (appToMerge && appToMerge.id && dynamicFields?.dynamicFields) {
        // Парсимо dynamicFields з апки (з бази)
        let appDynamicFields: Array<{ name: string, value: string }> = [];
        if (appToMerge.dynamicFields) {
          try {
            if (typeof appToMerge.dynamicFields === 'string') {
              appDynamicFields = JSON.parse(appToMerge.dynamicFields);
            } else if (Array.isArray(appToMerge.dynamicFields)) {
              appDynamicFields = appToMerge.dynamicFields;
            }
          } catch (error) {
            console.warn("Failed to parse dynamicFields from app:", error);
          }
        }

        const dbValuesMap = new Map<string, string>();
        for (const field of appDynamicFields) {
          const fieldName = field.name?.toUpperCase();
          if (fieldName && field.value !== undefined && field.value !== null) {
            dbValuesMap.set(fieldName, String(field.value));
          }
        }

        dynamicFieldsForForm = {
          ...dynamicFields,
          dynamicFields: dynamicFields.dynamicFields.map((field: any) => {
            const fieldNameUpper = field.name?.toUpperCase();
            const dbValue = dbValuesMap.get(fieldNameUpper);
            if (dbValue !== undefined) {
              return { ...field, defaultValue: dbValue };
            }
            return field;
          }),
        };
      }

      const fieldsValues: Record<string, string> = {}
      const formDynamicFields: Record<string, any> = {}

      // Якщо appForMerge передано — завантажуємо з БД (sync після Cancel), не підставляємо поточні значення форми (там можуть лишитись незбережені зміни).
      // Якщо skipAppId = true (зміна шаблону) — не зберігаємо старі значення.
      const shouldPreserveValues = !skipAppId && !appForMerge;
      const existingFormDynamicValues = shouldPreserveValues
        ? (form.getFieldValue("dynamicFields") || {})
        : {};
      const existingStateValues = shouldPreserveValues
        ? (dynamicFieldsValues || {})
        : {};

      if (dynamicFieldsForForm?.dynamicFields) {
        dynamicFieldsForForm.dynamicFields.forEach((field: any) => {
          const fieldNameLower = field.name.toLowerCase();
          const fieldNameUpper = field.name.toUpperCase()

          // В режимі редагування використовуємо defaultValue, який вже містить значення з бази
          // (якщо воно було підставлене вище)
          let existingValue = undefined;
          if (shouldPreserveValues) {
            existingValue =
              existingFormDynamicValues[field.name] ??
              existingFormDynamicValues[fieldNameLower] ??
              existingFormDynamicValues[fieldNameUpper] ??
              existingStateValues[field.name] ??
              existingStateValues[fieldNameLower] ??
              existingStateValues[fieldNameUpper] ??
              (appToMerge && appToMerge.id ? field.defaultValue : undefined);
          } else if (appToMerge && appToMerge.id && field.defaultValue) {
            existingValue = field.defaultValue;
          }

          if (
            existingValue !== undefined &&
            existingValue !== null &&
            existingValue !== ""
          ) {
            const stringValue =
              typeof existingValue === "string"
                ? existingValue
                : String(existingValue);
            fieldsValues[fieldNameLower] = stringValue;
            fieldsValues[fieldNameUpper] = stringValue;
            fieldsValues[field.name] = stringValue;
            formDynamicFields[fieldNameLower] = parseDynamicFieldValue(existingValue, field.type);
            return;
          }

          if (field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
            fieldsValues[fieldNameLower] = String(field.defaultValue)
            fieldsValues[fieldNameUpper] = String(field.defaultValue)
            fieldsValues[field.name] = String(field.defaultValue)

            const parsedDefaultValue = parseDynamicFieldValue(field.defaultValue, field.type);
            formDynamicFields[fieldNameLower] = parsedDefaultValue;
          }
        })
      }

      setDynamicFieldsValues(fieldsValues)

      // Відновлюємо збережені значення полів (тільки ті, що існують в новому шаблоні)
      // Якщо skipAppId = true, не зберігаємо старі значення
      if (Object.keys(formDynamicFields).length > 0) {
        const finalFormValues = shouldPreserveValues
          ? {
            ...(existingFormDynamicValues || {}),
            ...formDynamicFields,
          }
          : formDynamicFields; // При зміні шаблону використовуємо тільки нові значення (без старих)
        form.setFieldValue("dynamicFields", finalFormValues);
      } else {
        // Якщо немає нових значень (немає дефолтів), очищаємо форму
        // Особливо важливо при skipAppId = true (зміна шаблону)
        form.setFieldValue("dynamicFields", {});
      }

      // Зберігаємо версію шаблону для відстеження змін
      const selectedTemplate = templates.find((t) => t.id === templateId);
      const currentVersion =
        selectedTemplate?.currentVersion || selectedTemplate?.version;
      setLastLoadedTemplateVersion(currentVersion || null);

      // Initialize endpoint mappings with defaults, but preserve existing user settings
      // Якщо endpointMappingsConfig вже встановлений (наприклад, з збережених даних застосунку),
      // використовуємо його як основу
      // 🔥 ВИПРАВЛЕННЯ: Перевіряємо також значення в формі перед перезаписом
      const formMappings = form.getFieldValue("endpointMappings") || [];
      const hasFormMappings = Array.isArray(formMappings) && formMappings.length > 0;

      if (forceReload || (endpointMappingsConfig.length === 0 && !hasFormMappings)) {
        const currentMappings =
          hasFormMappings ? formMappings :
            endpointMappingsConfig.length > 0 ? endpointMappingsConfig : [];

        const initialEndpointMappings: EndpointMapping[] =
          dynamicFields.endpoints.map((endpoint) => {
            // Знаходимо існуючі налаштування для цього ендпоінту
            const existingMapping = currentMappings.find(
              (existing) => existing.handler === endpoint.name
            );

            return {
              handler: endpoint.name,
              path: existingMapping?.path || endpoint.path,
              method: existingMapping?.method || endpoint.method,
              enabled:
                existingMapping?.enabled !== undefined
                  ? existingMapping.enabled
                  : endpoint.enabled,
              subdomain: existingMapping?.subdomain || "",
              // Зберігаємо offer налаштування якщо вони були
              isOffer:
                existingMapping?.isOffer !== undefined
                  ? existingMapping.isOffer
                  : endpoint.isOffer,
              offerResponseType: existingMapping?.offerResponseType || "redirect",
              offerResponseCode: existingMapping?.offerResponseCode || 200,
              offerUrl: existingMapping?.offerUrl || "",
              offerErrorType: existingMapping?.offerErrorType || "redirect",
              offerErrorCode: existingMapping?.offerErrorCode || 400,
              offerError: existingMapping?.offerError || "",
              errorStaticSiteId: existingMapping?.errorStaticSiteId || "",
              errorLandingIndexPath: existingMapping?.errorLandingIndexPath || "index.html",
            };
          });
        setEndpointMappingsConfig(initialEndpointMappings);
        form.setFieldsValue({ endpointMappings: initialEndpointMappings });

        // Якщо це forceReload, збільшуємо ключ для форсування оновлення EndpointMappings
        if (forceReload) {
          setEndpointMappingsKey((prev) => prev + 1);
        }
      }

      // Initialize dynamic values with defaults (але зберігаємо існуючі значення)

      if (!isEditMode || !editingApp) { }
      const initialValues: Record<string, string> = {};
      dynamicFields.dynamicFields.forEach((field) => {
        const fieldNameLower = field.name.toLowerCase()
        const fieldNameUpper = field.name.toUpperCase()

        const hasValue = fieldsValues[field.name] || fieldsValues[fieldNameLower] || fieldsValues[fieldNameUpper]

        if (field.type === "encryption" && !hasValue) {
          // Auto-generate encryption key тільки якщо поле ще не має значення або якщо це force reload
          const chars =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
          let result = "";
          for (let i = 0; i < 32; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          initialValues[field.name] = result;
          initialValues[fieldNameLower] = result;
          initialValues[fieldNameUpper] = result;
          // APP_ID тепер автогенерується через defaultValue в шаблоні
        } else if (
          field.defaultValue !== undefined &&
          field.defaultValue !== null &&
          field.defaultValue !== '' &&
          !hasValue
        ) {
          initialValues[field.name] = field.defaultValue.toString()
          initialValues[fieldNameLower] = field.defaultValue.toString()
          initialValues[fieldNameUpper] = field.defaultValue.toString()
        }
      });

      // Оновлюємо тільки нові поля, зберігаючи існуючі (крім випадку forceReload для нових полів)
      setDynamicFieldsValues((prev) => {
        return { ...prev, ...initialValues }
      });
    } catch (error) {
      console.error("Error loading template dynamic fields:", error);
      message.error("Помилка завантаження динамічних полів шаблону");
    }
  };

  const handleCreateApp = () => {
    navigate("/apps/create");
  };

  const handleEditApp = (app: App) => {
    navigate(`/apps/edit/${app.id}`);
  };

  const handleDeleteApp = async (appId: string) => {
    try {
      await deleteAppMutation.mutateAsync(appId);
      if (editingApp?.id === appId) {
        form.resetFields();
        resetTemplateState({
          resetEndpointMappingsKey: 'increment',
        });
      }
      message.success(t("apps.deleteSuccess"));
    } catch (error) {
      console.error(t("apps.deleteError"), error);
      message.error(
        `${t("apps.deleteError")}: ${error instanceof Error ? error.message : t("common.error")
        }`
      );
    }
  };

  const handleDeployApp = (appId: string) => {
    Modal.confirm({
      title: "Підтвердження деплою",
      content:
        "Ви впевнені що хочете розгорнути цей додаток? Це може змінити роботу активного застосунку.",
      icon: <ExclamationCircleOutlined />,
      okText: "Так, розгорнути",
      cancelText: "Скасувати",
      onOk: async () => {
        try {
          await deployAppMutation.mutateAsync(appId);
          message.success(t("apps.deploySuccess"));
        } catch (error: unknown) {
          const err = error as { response?: { status?: number } };
          setSingleDeployError({
            appId,
            error: extractApiError(error),
            statusCode: err?.response?.status,
          });
        }
      },
    });
  };

  const handleDeployBulkClick = () => {
    if (selectedRowKeys.length === 0) return;
    const limit = 150;
    if (selectedRowKeys.length > limit) {
      message.warning(t("apps.bulkLimitWarning", { limit }));
      return;
    }
    Modal.confirm({
      title: t("apps.bulkDeployTitle"),
      content: t("apps.bulkDeployConfirm", { count: selectedRowKeys.length }),
      icon: <ExclamationCircleOutlined />,
      okText: t("apps.bulkDeployOk"),
      cancelText: t("apps.cancel"),
      onOk: async () => {
        try {
          const result = await deployBulkMutation.mutateAsync({
            appIds: selectedRowKeys,
            confirmed: true,
          });
          setBulkActionResult({ kind: "deploy", result });
        } catch (error) {
          message.error(t("apps.deployError"));
        }
      },
    });
  };

  const handleDeleteBulkClick = () => {
    if (selectedRowKeys.length === 0) return;
    const limit = 150;
    if (selectedRowKeys.length > limit) {
      message.warning(t("apps.bulkLimitWarning", { limit }));
      return;
    }
    Modal.confirm({
      title: t("apps.bulkDeleteTitle"),
      content: t("apps.bulkDeleteConfirm", { count: selectedRowKeys.length }),
      icon: <ExclamationCircleOutlined />,
      okText: t("apps.delete"),
      okButtonProps: { danger: true },
      cancelText: t("apps.cancel"),
      onOk: async () => {
        try {
          const result = await deleteBulkMutation.mutateAsync({
            appIds: selectedRowKeys,
            confirmed: true,
          });
          setBulkActionResult({ kind: "delete", result });
        } catch (error) {
          message.error(t("apps.deleteError"));
        }
      },
    });
  };

  const handleViewWorkerCode = (appId: string) => {
    setWorkerCodeAppId(appId);
  };

  const handleCloseWorkerCodeModal = () => {
    setWorkerCodeAppId(null);
    setWorkerCodeLoading(false);
  };

  const handleSubmit = async (values: any) => {
    // Захист від подвійного виклику
    if (isSubmitting) {
      return;
    }
    try {
      setIsSubmitting(true);
      setLoadingAction(true);

      // Endpoint conflicts will be validated in routing section
      const landingRouteForCheck: RouteLike[] = (
        values.landingRoutes || []
      ).map((r: any) => ({
        subdomain: r?.subdomain,
        basePath: r?.basePath,
        enabled: r?.enabled !== false,
      }));
      const endpointMappingsForm: MappingLike[] = (
        values.endpointMappings || []
      ).filter((m: any) => m && m.path);

      const endpointMappingsRaw: any[] = (values.endpointMappings || []) as any[];
      const jsonErrors = validateEndpointMappingsForJson(endpointMappingsRaw);
      if (jsonErrors.length > 0) {
        Modal.error({
          title: "Помилка валідації JSON",
          content: (
            <div>
              У конфігурації ендпоінтів знайдено невалідний JSON:
              <ul style={{ marginTop: 8 }}>
                {jsonErrors.map((e, i) => (
                  <li key={i}>
                    <code>{e.handler}</code> — поле <b>{e.field}</b>: {e.message}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 8, color: "#666" }}>
                Порожні поля дозволені. Виправте JSON або очистіть поле.
              </div>
            </div>
          ),
        });
        setLoadingAction(false);
        return;
      }

      const conflicts = findApiVsStaticConflicts(
        landingRouteForCheck,
        endpointMappingsForm
      );
      if (conflicts.length) {
        const domainName =
          values.domain ||
          form.getFieldValue("domain") ||
          editingApp?.domain ||
          "domain.com";
        Modal.error({
          title: "Конфлікт шляхів",
          content: (
            <div>
              На одному і тому ж шляху вже є API або лендінг:
              <ul style={{ marginTop: 8 }}>
                {conflicts.map((c, i) => (
                  <li key={i}>
                    <code>
                      {c.sub ? `${c.sub}.` : ""}
                      {domainName}
                      {c.path}
                    </code>
                    {c.api ? ` (API: ${c.api})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
        setLoadingAction(false);
        return;
      }
      if (Array.isArray(staticSites) && staticSites.length > 0) {
        // Нормализуем staticSites в формат RouteLike
        const staticRoutesProbe: RouteLike[] = staticSites.map((s: any) => ({
          subdomain: s?.subdomain ?? "",
          basePath: s?.basePath ?? "/",
          enabled: s?.status !== StaticSiteStatus.INACTIVE,
        }));

        const conflictsWithStatic = findApiVsStaticConflicts(staticRoutesProbe, (form.getFieldValue("endpointMappings") || []) as any);

        if (conflictsWithStatic.length) {
          const domainName =
            values.domain || form.getFieldValue("domain") || editingApp?.domain || "domain.com";

          Modal.error({
            title: "Конфлікт шляхів зі статичними сайтами",
            content: (
              <div>
                На одному і тому ж шляху вже є статичний лендінг:
                <ul style={{ marginTop: 8 }}>
                  {conflictsWithStatic.map((c, i) => (
                    <li key={i}>
                      <code>
                        {c.sub ? `${c.sub}.` : ""}{domainName}{c.path}
                      </code>
                      {c.api ? ` (API: ${c.api})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          });

          setLoadingAction(false);
          return;
        }
      }



      // Prepare app data with dynamic fields and endpoint mappings
      // Джерело правди: форма (values.dynamicFields) + стейт (dynamicFieldsValues), щоб нічого не втратити
      const formDynamicFields = values.dynamicFields && typeof values.dynamicFields === "object" ? values.dynamicFields : {};
      const mergedDynamicValues: Record<string, string> = {
        ...dynamicFieldsValues,
        ...Object.fromEntries(
          Object.entries(formDynamicFields).map(([k, v]) => [
            k,
            v !== undefined && v !== null ? String(v) : "",
          ])
        ),
      };

      // Список полів поточного шаблону (для фільтрації старих полів)
      const currentTemplateMacros =
        effectiveTemplateDynamicFields?.dynamicFields?.map((field) =>
          field.name.toLowerCase()
        ) || [];

      // Виключаємо endpoint; залишаємо поля з шаблону, або всі заповнені якщо шаблон ще без списку полів
      const dynamicFields: DynamicField[] = Object.entries(mergedDynamicValues)
        .filter(([name]) => {
          if (name === "endpoint") return false;
          if (currentTemplateMacros.length > 0) {
            return currentTemplateMacros.includes(name.toLowerCase());
          }
          return true;
        })
        .map(([name, value]) => ({
          name: name.toUpperCase(),
          value: (value !== undefined && value !== null ? String(value) : "") as string,
        }));

      // Генеруємо endpoint з динамічних полів або використовуємо дефолтний
      const endpoint =
        mergedDynamicValues.endpoint || values.endpoint || "webhook";

      // Формуємо config об'єкт на основі макросів шаблону
      const config = {
        // Зберігаємо стандартні поля з values
        ...Object.keys(values).reduce((acc, key) => {
          if (
            [
              "affiseAppId",
              "affisePkgAppName",
              "secretId",
              "affiseApiKey",
              "PUSH_PLATFORM_APP_TOKEN",
            ].includes(key)
          ) {
            acc[key] = values[key];
          }
          return acc;
        }, {} as Record<string, string>),
      };

      // Валідація: перевірка чи вибрано статичний сайт для типу відповіді "render"
      const renderErrors: Array<{ handler: string; field: string }> = [];
      // Використовуємо values.endpointMappings як основний джерело, endpointMappingsConfig як fallback
      // Після синхронізації та зміни налаштувань користувачем, дані зберігаються в формі, але endpointMappingsConfig може бути очищений
      const formMappings = Array.isArray(values.endpointMappings) ? values.endpointMappings : [];
      const configMappings = Array.isArray(endpointMappingsConfig) ? endpointMappingsConfig : [];
      const endpointMappingsForValidation = formMappings.length > 0 ? formMappings : configMappings;
      const normalizedMappings = endpointMappingsForValidation.map((m: any) => {
        if (m.errorStaticSiteId !== undefined) {
          return m;
        }

        return inflateFromApi(m)
      })
      normalizedMappings.forEach((m: any) => {
        if (m?.offerErrorType === "render" || m?.offerErrorType === "landing") {
          const errorStaticSiteId = m?.errorStaticSiteId;
          if (!errorStaticSiteId || (typeof errorStaticSiteId === "string" && !errorStaticSiteId.trim())) {
            renderErrors.push({
              handler: m?.handler || "невідомий",
              field: "errorStaticSiteId",
            });
          }
        }
      });

      if (renderErrors.length > 0) {
        Modal.error({
          title: "Помилка валідації налаштувань відповідей",
          content: (
            <div>
              Для типу відповіді <b>Landing(HTML)</b> необхідно вибрати статичний сайт:
              <ul style={{ marginTop: 8 }}>
                {renderErrors.map((e, i) => (
                  <li key={i}>
                    <code>{e.handler}</code> — поле <b>{e.field}</b>: не вибрано статичний сайт
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 8, color: "#666" }}>
                Оберіть статичний сайт у полі "Лендінг (static site)" для кожного ендпоінту з типом відповіді "Landing(HTML)".
              </div>
            </div>
          ),
        });
        setLoadingAction(false);
        return;
      }

      const normalizedMappingsForBackend = normalizedMappings.map(mapEndpointMappingToBackend);

      const resolvedCustomId = (values.customId || editingApp?.customId || '').toString().trim().toLowerCase()
      if (!editingApp) {
        const allowedNorm = allowedAppIds.map((id) => String(id).trim().toLowerCase())
        if (!resolvedCustomId || !allowedNorm.length || !allowedNorm.includes(resolvedCustomId)) {
          message.error("Оберіть Custom App ID зі списку або натисніть «Оновити» для завантаження списку з CRM.")
          return
        }
      }
      const appData = {
        name: values.name,
        description: values.description,
        templateId: values.templateId || selectedTemplateId,
        greyType: values.greyType,
        domain: values.domain || window.location.hostname,
        endpoint, // Додаємо поле endpoint
        responseType: values.responseType || ResponseType.JSON,
        offerUrl: values.offerUrl,
        offerError: values.offerError,
        errorRedirectUrl: values.errorRedirectUrl,
        config, // Правильно структурований config
        dynamicFields,
        endpointMappings: normalizedMappingsForBackend,
        customId: resolvedCustomId
      };

      if (editingApp) {
        await updateAppMutation.mutateAsync({ id: editingApp.id, data: appData });
        message.success(t("apps.updateSuccess"));
      } else {
        const createdApp = await createAppMutation.mutateAsync(appData);
        message.success(t("apps.createSuccess"));
        setLastLoadedTemplateVersion(null);
        navigate(`/apps/edit/${createdApp.id}`);
        return;
      }

      // Не очищаємо форму та стейт ПЕРЕД навігацією, щоб уникнути race condition
      setLastLoadedTemplateVersion(null);
      navigate("/apps");
      resetTemplateState({
        resetPreviousTemplateId: true,
        resetEndpointMappingsKey: true,
      });
      form.resetFields();
    } catch (error) {
      const { title, content } = getApiErrorForModal(error);
      Modal.error({
        title,
        content: content || (editingApp ? t("apps.updateError") : t("apps.createError")),
      });
    } finally {
      setLoadingAction(false);
      setIsSubmitting(false); // Скидаємо прапорець після завершення
    }
  };

  // Якщо це режим створення/редагування - показуємо форму
  if (isFormMode) {
    return (
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={2}>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  navigate("/apps");
                }}
                type="text"
              />
              {isCreateMode ? t("apps.create") : t("apps.edit")}
            </Space>
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ width: "100%", maxWidth: "1400px" }}
        >
          <Tabs
            defaultActiveKey="basic"
            onChange={(activeKey) => {
              if (activeKey === "routing" && selectedTemplateId) {
                const formMappings = form.getFieldValue("endpointMappings") || [];
                const configMappings = endpointMappingsConfig || [];
                const hasEndpoints =
                  effectiveTemplateDynamicFields?.endpoints &&
                  effectiveTemplateDynamicFields.endpoints.length > 0;
                if (!hasEndpoints) {
                  const appIdForEndpoints = editingApp?.id;
                  loadTemplateEndpoints(selectedTemplateId, false, appIdForEndpoints);
                } else {
                  if (formMappings.length > 0 && configMappings.length === 0) {
                    setEndpointMappingsConfig(formMappings);
                    setEndpointMappingsKey((k) => k + 1);
                  }
                }
              }
            }}
            items={[
              {
                key: "basic",
                label: t("apps.basicInfo"),
                forceRender: true,
                children: (
                  <BasicInfoTab
                    form={form}
                    editingApp={editingApp}
                    isEditMode={isEditMode}
                    templates={templates}
                    appsQueryLoading={appsQuery.isLoading}
                    refreshAppsMutation={refreshAppsMutation}
                    existingCustomIds={existingCustomIds}
                    allowedAppIds={allowedAppIds}
                  />
                ),
              },
              {
                key: "config",
                label: "⚙️ Конфігурація",
                children: (
                  <ConfigTab
                    form={form}
                    selectedTemplateId={selectedTemplateId}
                    dynamicFieldsValues={dynamicFieldsValues}
                    setDynamicFieldsValues={setDynamicFieldsValues}
                    effectiveTemplateDynamicFields={effectiveTemplateDynamicFields}
                  />
                ),
              },
              {
                key: "routing",
                label: "🌐 Роутинг",
                forceRender: true,
                children: (
                  <RoutingTab
                    form={form}
                    editingApp={editingApp ?? undefined}
                    isEditMode={isEditMode}
                    editAppId={editAppId}
                    selectedTemplateId={selectedTemplateId}
                    loadTemplateEndpoints={loadTemplateEndpoints}
                    loadingEndpoints={loadingEndpoints}
                    syncEndpointsMutation={syncEndpointsMutation as RoutingTabProps["syncEndpointsMutation"]}
                    loading={loading}
                    endpointMappingsKey={endpointMappingsKey}
                    initialMappingsFromDb={isEditMode ? (editEndpointMappingsFromDbRef.current ?? undefined) : undefined}
                    endpointMappingsValue={(() => {
                      const formMappings = form.getFieldValue("endpointMappings") || [];
                      const configMappings = endpointMappingsConfig || [];
                      const fromDb = isEditMode ? editEndpointMappingsFromDbRef.current : null;
                      if (fromDb && fromDb.length > 0) return fromDb;
                      return configMappings.length > 0 ? configMappings : formMappings;
                    })()}
                    onEndpointMappingsChange={(mappings) => {
                      form.setFieldsValue({ endpointMappings: mappings });
                      setEndpointMappingsConfig(mappings);
                    }}
                    onSyncEndpointsSuccess={(endpoints) => {
                      const currentTemplateId = form.getFieldValue("templateId");
                      form.setFieldsValue({
                        endpointMappings: endpoints,
                        templateId: currentTemplateId,
                      });
                      flushSync(() => {
                        setEndpointMappingsConfig(endpoints);
                        setEndpointMappingsKey((prev) => prev + 1);
                      });
                    }}
                    effectiveTemplateDynamicFields={effectiveTemplateDynamicFields}
                    staticSites={staticSites}
                    dynamicFieldsForEndpoints={convertDynamicFieldsToArray(dynamicFieldsWatch)}
                  />
                ),
              },
              {
                key: "landingCreation",
                label: "Створення лендингу",
                forceRender: true,
                children: (
                    <LandingCreationContentTab
                    key={isEditMode ? id ?? "edit" : "create"}
                    form={form}
                    editingApp={editingApp}
                    isEditMode={isEditMode}
                    isCreateMode={isCreateMode}
                    staticSites={staticSites}
                  />
                ),
              },
              {
                key: "domainFiles",
                label: "📦 Файли домену",
                children: (
                  <DomainFilesTab
                    editingApp={editingApp}
                    isEditMode={isEditMode}
                  />
                ),
              },
            ]}
          />

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={async () => {
                resetTemplateState({
                  resetFormFields: true,
                  resetEndpointMappingsKey: 'increment',
                });
                setDynamicFieldsValues({});
                form.resetFields();
                form.setFieldsValue({ dynamicFields: {} });
                navigate("/apps");
              }}>
                {t("apps.cancel")}
              </Button>
              {isFormMode && (
                <Button
                  onClick={() => setLoadTemplateModalOpen(true)}
                  icon={<FileTextOutlined />}
                >
                  Завантажити пресет
                </Button>
              )}
              {isFormMode && (
                <Button
                  onClick={() => setSaveTemplateModalOpen(true)}
                  icon={<SaveOutlined />}
                >
                  Зберегти як пресет
                </Button>
              )}
              {isEditMode && editingApp?.id && (
                <Button
                  danger
                  loading={purgeLfCacheMutation.isPending}
                  onClick={() => purgeLfCacheMutation.mutate(editingApp.id)}
                  title="Скинути Redis-кеш LinkFactory для домену цієї апки"
                >
                  Скинути кеш LF
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {t("apps.save")}
              </Button>
            </Space>
          </div>

          <SaveAsTemplateModal
            open={saveTemplateModalOpen}
            onClose={() => setSaveTemplateModalOpen(false)}
            appId={editingApp?.id}
            getFormData={() => {
              // Беремо актуальний роутинг з форми (вкладка "Роутинг"):
              // спочатку форма, fallback — endpointMappingsConfig
              const formMappings = form.getFieldValue("endpointMappings");
              const source =
                Array.isArray(formMappings) && formMappings.length > 0
                  ? formMappings
                  : Array.isArray(endpointMappingsConfig)
                    ? endpointMappingsConfig
                    : [];
              const inflated = source.map((m: any) =>
                m?.errorStaticSiteId !== undefined ? m : inflateFromApi(m)
              );
              return {
                templateId: form.getFieldValue("templateId") || editingApp?.templateId,
                greyType: form.getFieldValue("greyType") || editingApp?.greyType,
                endpointMappings: inflated.map(mapEndpointMappingToBackend),
                landingRoutes: parseJsonArray(form.getFieldValue("landingRoutes")),
              };
            }}
          />

          <LoadTemplateModal
            open={loadTemplateModalOpen}
            onClose={() => setLoadTemplateModalOpen(false)}
            templates={appConfigTemplatesQuery.data ?? []}
            loading={appConfigTemplatesQuery.isLoading}
            onSelect={(tmpl) => {
              // Застосовуємо тільки роутинг пресету (ендпоінти + лендинг-маршрути),
              // конфігурацію (домен, динамічні поля, config) не чіпаємо.
              // endpointMappings/landingRoutes можуть лежати як JSON-рядок — парсимо.
              // inflateFromApi — автоформатування JSON у offerUrl/offerError (pretty-print),
              // як і при завантаженні апки з БД: компактний JSON одразу стає багаторядковим.
              const presetEndpointMappings = parseJsonArray(tmpl.endpointMappings).map((m: any) => inflateFromApi(m));
              const presetLandingRoutes = parseJsonArray(tmpl.landingRoutes);
              // Встановлюємо значення ДО зміни templateId,
              // щоб useEffect на selectedTemplateId побачив їх при мерджі
              form.setFieldsValue({
                greyType: tmpl.greyType || "api",
                endpointMappings: presetEndpointMappings,
                landingRoutes: presetLandingRoutes,
              });
              if (presetEndpointMappings.length > 0) {
                setEndpointMappingsConfig(presetEndpointMappings as any);
                setEndpointMappingsKey((k) => k + 1);
              }
              // Тепер змінюємо templateId — це тригерить useEffect.
              // Захист (isPresetLoadingRef + presetTemplateIdRef) тримається,
              // доки користувач сам не обере інший шаблон — жоден ефект
              // не зможе затерти роутинг пресету дефолтами шаблону.
              isPresetLoadingRef.current = true;
              presetTemplateIdRef.current = tmpl.templateId || "";
              form.setFieldsValue({
                templateId: tmpl.templateId || "",
              });
            }}
          />
        </Form>
      </div>
    );
  }

  // Список апок — таблиця через AppsTable
  return (
    <div>
      <AppsTable
        apps={apps}
        loading={loading}
        templates={templates}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        onCreate={handleCreateApp}
        onEdit={handleEditApp}
        onDeploy={handleDeployApp}
        onDelete={handleDeleteApp}
        onViewWorkerCode={handleViewWorkerCode}
        onDeployBulk={handleDeployBulkClick}
        onDeleteBulk={handleDeleteBulkClick}
        deployBulkPending={deployBulkMutation.isPending}
        deleteBulkPending={deleteBulkMutation.isPending}
        workerCodeAppId={workerCodeAppId}
        workerCodeLoading={workerCodeLoading}
      />

      <WorkerCodeModal
        appId={workerCodeAppId}
        onClose={handleCloseWorkerCodeModal}
        onLoadingChange={setWorkerCodeLoading}
      />

      <Modal
        title="Помилка деплою"
        open={singleDeployError !== null}
        onCancel={() => setSingleDeployError(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSingleDeployError(null)}>
            {t("apps.close")}
          </Button>,
        ]}
        width={560}
      >
        {singleDeployError && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Text strong>Застосунок</Text>
              <Space direction="vertical" size={0}>
                <Text code>{singleDeployError.appId}</Text>
                {(() => {
                  const app = apps.find((a) => a.id === singleDeployError.appId);
                  return app?.name ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>{app.name}</Text>
                  ) : null;
                })()}
              </Space>
            </Space>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Text strong>Помилка</Text>
              <Text
                type="danger"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  display: "block",
                }}
              >
                {singleDeployError.error}
              </Text>
            </Space>
            {singleDeployError.statusCode != null && (
              <Text type="secondary">Код відповіді: {singleDeployError.statusCode}</Text>
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title={bulkActionResult?.kind === "delete" ? t("apps.bulkResultTitleDelete") : t("apps.bulkResultTitleDeploy")}
        open={bulkActionResult !== null}
        onCancel={() => {
          setBulkActionResult(null);
          setSelectedRowKeys([]);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setBulkActionResult(null);
              setSelectedRowKeys([]);
            }}
          >
            {t("apps.close")}
          </Button>,
        ]}
        width={720}
      >
        {bulkActionResult && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <Text strong>Всього:</Text> {bulkActionResult.result.total}
              {" · "}
              <Text type="success">Успішно:</Text> {bulkActionResult.result.succeeded}
              {" · "}
              <Text type="danger">Помилки:</Text> {bulkActionResult.result.failed}
            </div>
            {bulkActionResult.result.results.length > 0 && (
              <>
                <Divider orientation="left">Деталі помилок</Divider>
                <Table
                  size="small"
                  dataSource={bulkActionResult.result.results}
                  rowKey="appId"
                  pagination={false}
                  scroll={{ y: 320 }}
                  columns={[
                    {
                      title: "App",
                      key: "app",
                      width: 200,
                      render: (_: unknown, row: { appId: string }) => {
                        const app = apps.find((a) => a.id === row.appId);
                        return (
                          <Space direction="vertical" size={0}>
                            <Text code>{row.appId}</Text>
                            {app?.name && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {app.name}
                              </Text>
                            )}
                          </Space>
                        );
                      },
                    },
                    {
                      title: "Помилка",
                      dataIndex: "error",
                      key: "error",
                      render: (text: string) => (
                        <Text
                          type="danger"
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            display: "block",
                          }}
                        >
                          {text}
                        </Text>
                      ),
                    },
                    {
                      title: "Код",
                      dataIndex: "statusCode",
                      key: "statusCode",
                      width: 80,
                    },
                  ]}
                />
              </>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};
